import { useAtomValue } from 'jotai';
import { BACKGROUND_COLOR, buildPolygonStyle } from 'lib/map';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import { createMapSourceAtom } from './atoms';
import { MapUserConfigAtom } from './types';
import { standardMapStyles } from './map-styles';
import { useMemo } from 'react';

const STYLE = buildPolygonStyle({
  color: BACKGROUND_COLOR,
  fillOpacity: 0.05,
  lineOpacity: 0.5,
});

type DevelopmentAreaName = string;

interface Props {
  style?: typeof STYLE;
  excluding?: DevelopmentAreaName[];
  mapConfigAtom: MapUserConfigAtom;
}

const DevelopmentAreasLayer = ({
  style = STYLE,
  excluding = [],
  mapConfigAtom,
}: Props) => {
  const devAreasSourceAtom = useMemo(
    () => createMapSourceAtom('developmentAreas'),
    []
  );

  const excludedNameFilter = ['!in', 'name', ...excluding];

  const devAreasSource = useAtomValue(devAreasSourceAtom);
  const mapConfig = useAtomValue(mapConfigAtom);
  if (!devAreasSource) {
    return null;
  }

  return (
    <LayerBand band={LAYER_ORDER.developmentAreas}>
      <Source id="development-areas" type="vector" url={devAreasSource}>
        {mapConfig.developmentAreas.options.horizontalUnits && (
          <Layer
            id="development-area-horizontal-fill"
            type="fill"
            paint={style.fill}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'horizontal'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.horizontalUnits && (
          <Layer
            id="development-area-horizontal-line"
            type="line"
            paint={style.line}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'horizontal'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.verticalUnits && (
          <Layer
            id="development-area-vertical-fill"
            type="fill"
            paint={style.fill}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'vertical'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.verticalUnits && (
          <Layer
            id="development-area-vertical-line"
            type="line"
            paint={style.line}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'vertical'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.fieldUnits && (
          <Layer
            id="development-area-field-fill"
            type="fill"
            paint={style.fill}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'spraberry unit'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.fieldUnits && (
          <Layer
            id="development-area-field-line"
            type="line"
            paint={style.line}
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'unit_type', 'spraberry unit'],
            ]}
            source-layer="development-areas"
          />
        )}
        {mapConfig.developmentAreas.options.pricing && (
          <Layer
            id="development-area-pricing"
            type="symbol"
            source-layer="development-areas"
            minzoom={12}
            filter={[
              'all',
              ['==', 'type', 'centroid'],
              ['==', 'unit_type', 'horizontal'],
            ]}
            layout={{
              'text-field': [
                'case',
                ['!', ['has', 'max']],
                [
                  'format',
                  ['upcase', ['get', 'name']],
                  {
                    'font-scale': 1,
                    'text-font': [
                      'literal',
                      standardMapStyles[0].fonts.CONDENSED,
                    ],
                  },
                  '\n',
                  {},
                  'No Evaluation',
                  {
                    'font-scale': 0.8,
                    'text-color': 'red',
                    'text-font': [
                      'literal',
                      standardMapStyles[0].fonts.CONDENSED_BOLD,
                    ],
                  },
                ],
                [
                  'format',
                  ['upcase', ['get', 'name']],
                  {
                    'font-scale': 1,
                    'text-font': [
                      'literal',
                      standardMapStyles[0].fonts.CONDENSED,
                    ],
                  },
                  '\n',
                  {},
                  [
                    'concat',
                    'TRGT: $',
                    ['number-format', ['get', 'target'], {}],
                  ],
                  {
                    'font-scale': 0.8,
                    'text-font': [
                      'literal',
                      standardMapStyles[0].fonts.CONDENSED_BOLD,
                    ],
                  },
                  '\n',
                  {},
                  ['concat', 'MAX: $', ['number-format', ['get', 'max'], {}]],
                  {
                    'font-scale': 0.8,
                    'text-font': [
                      'literal',
                      standardMapStyles[0].fonts.CONDENSED_BOLD,
                    ],
                  },
                ],
              ],
              'text-justify': 'left',
              'text-size': {
                base: 1,
                stops: [
                  [12, 8],
                  [18, 24],
                ],
              },
            }}
            paint={{
              'text-color': 'black',
            }}
          />
        )}
      </Source>
    </LayerBand>
  );
};

export { DevelopmentAreasLayer };
